import { z } from "zod";
import { fatorDeCorrecaoSchema, quantidadeSchema } from "./material";

/**
 * Consumo de material de um serviço em produção.
 *
 * A ficha técnica é previsão; isto é o gasto. Por isso quantidade e fator são editáveis aqui e o
 * material pode ser trocado, acrescentado ou removido — o protético registra o que realmente saiu
 * da prateleira, que é o número de que o estoque precisa.
 */

export const servicoIdSchema = z.object({ serviceId: z.string().min(1) });
export const consumoIdSchema = z.object({ consumptionId: z.string().min(1) });

export const aplicarFichaSchema = z.object({
  serviceId: z.string().min(1),
  technicalSheetId: z.string().min(1),
  /** Sem isto, aplicar por cima de consumo já registrado é recusado: apagar gasto real por engano
   *  é pior que um clique a mais. */
  substituir: z.boolean().optional(),
});
export type AplicarFichaInput = z.infer<typeof aplicarFichaSchema>;

/** Resolver a decisão em aberto: qual bloco saiu da prateleira. */
/** Que máquinas foram usadas de fato. Substitui a lista inteira; as linhas que a ficha registrou
 *  não se mexem — elas são o registro do que a receita mandava. */
export const definirEquipamentosSchema = z.object({
  serviceId: z.string().min(1),
  equipmentIds: z.array(z.string().min(1)).max(20),
});
export type DefinirEquipamentosInput = z.infer<typeof definirEquipamentosSchema>;

export const resolverLinhaSchema = z.object({
  consumptionId: z.string().min(1),
  materialLineId: z.string().min(1),
});

/** Ajustar o que foi gasto de fato. Os dois campos opcionais — a tela edita um de cada vez. */
export const atualizarLinhaSchema = z.object({
  consumptionId: z.string().min(1),
  quantityNet: quantidadeSchema.optional(),
  correctionFactor: fatorDeCorrecaoSchema.optional(),
});

/**
 * Linha fora da ficha: o bloco que quebrou, o glaze extra.
 *
 * Aceita material exato OU categoria (nasce em aberto) — a mesma escolha da ficha, porque às vezes
 * se sabe que gastou "mais um bloco" antes de saber qual.
 */
export const adicionarLinhaSchema = z
  .object({
    serviceId: z.string().min(1),
    materialLineId: z.string().min(1).nullish(),
    materialCategoryId: z.string().min(1).nullish(),
    quantityNet: quantidadeSchema,
    correctionFactor: fatorDeCorrecaoSchema.default(1),
  })
  .refine((l) => !!l.materialLineId || !!l.materialCategoryId, {
    message: "Escolha um material ou uma categoria.",
  });
export type AdicionarLinhaInput = z.infer<typeof adicionarLinhaSchema>;
